import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { List } from "immutable";
import { map } from "rxjs/operators";
import { AutomapperService } from "../../../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../../../core/environment.tokens";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { FormService } from "../../../../dynamic-forms/form.service";
import { CheckboxGroup } from "../../../../dynamic-forms/inputs/checkbox-group/checkbox-group.model";
import { DynamicInput } from "../../../../dynamic-forms/inputs/dynamic-input.model";
import { SelectableInput } from "../../../../dynamic-forms/inputs/selectable-input.model";
import { Textbox } from "../../../../dynamic-forms/inputs/textbox/textbox.model";
import { DocumentPageViewerComponent } from "../../../../shared/document/document-page-viewer/document-page-viewer.component";
import { IFilter } from "../../../../shared/grid/basic-filter-applied/basic-filter-applied.component";
import { BulkAction } from "../../../../shared/grid/bulk-actions/bulk-action.model";
import { GridView } from "../../../../shared/grid/grid-menu/grid-views/grid-view.model";
import { GridPipeName } from "../../../../shared/grid/grid-pipe.enum";
import { GridColumnDefinition } from "../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../shared/grid/models/grid-configuration.model";
import { GridFilter } from "../../../../shared/grid/models/grid-filter.model";
import { GridRequest } from "../../../../shared/grid/models/grid-request.model";
import { ServerGridComponent } from "../../../../shared/grid/server-grid/server-grid.component";
import { ListItem } from "../../../../shared/list/list-item";
import { TagType } from "../../../../shared/tags/model/tag-type.enum";
import { TagService } from "../../../../shared/tags/tag.service";
import { ArrayHelper } from "../../../../utilities/contracts/array-helper";
import { ClinicalPageService } from "../../clinical/clinical-page/clinical-page.service";
import { ChartService } from "../../member/chase-detail/chase-detail-chart/chart.service";
import { DocumentTypeName } from "../../retrieval/retreival-document-review/document-type.enum";
import { RetrievalDocumentServiceService } from "../../retrieval/retrieval-document-service.service";
import { RetrievalPageService } from "../../retrieval/retrieval-page/retrieval-page.service";
import { ChaseQueryService } from "../chase-query/chase-query.service";
import { ApprovalCenterService } from "./approval-center.service";
import { DocumentApprovalItem } from "./bulk-document-approval-item-model";
import { BulkDocumentApprovalRequest } from "./bulk-document-approval-request";
import { DocumentApprovalSearchResponse } from "./document-approval-search-results";
import { DocumentApprovalState } from "./document-approval-state.enum";

@Component({
  selector: "app-approval-center",
  templateUrl: "./approval-center.component.html",
  styleUrls: ["./approval-center.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprovalCenterComponent implements OnInit {
  @ViewChild("docIdColumn", { static: true }) docIdColumn: TemplateRef<any>;
  @ViewChild("chaseIdColumn", { static: true }) chaseIdColumn: TemplateRef<any>;
  @ViewChild(ServerGridComponent, { static: true }) serverGridComponent: ServerGridComponent;
  refreshViews = new EventEmitter<GridView>(true);
  headerStatistics = List<ListItem>();
  headerTitle: string;
  selectedStat: string;
  statisticsFilter: string;
  uploadMethods: any[];
  showConfirmationForPursuitCreationOnApprove: boolean;
  approvalRowData: DocumentApprovalSearchResponse[];
  modalApprovalRowData: DocumentApprovalSearchResponse[];
  showPostApprovalMessage: boolean;
  isDocumentModalVisble = false;
  selectedDocId: number;
  approvalPursuitChaseId: number;
  serverGridConfiguration = new GridConfiguration();
  gridData: any[] = [];
  gridSelection: any[] = [];
  request: GridRequest;
  refreshGrid = new EventEmitter<null>(true);
  actions: BulkAction[];
  serverRequest: GridRequest;
  isViewDetailVisible = false;
  @ViewChild("pageviewer", { static: true }) documentViewer: DocumentPageViewerComponent;
  form: FormGroup;
  tagType = TagType.DOCUMENT;
  isManageTagModalVisible = false;
  totalEntityCount: number;
  entityName: string;
  selectedDocuments: any[];
  documentApprovalGridSelection: any;
  allAvailableTags: SelectableInput[];
  selectedObjectId: number;
  isChaseActivated = false;
  approvePopUpConfirmationText: string;

  get documentQueueId(): Textbox {
    return this.getInput("documentQueueId") as Textbox;
  }
  get retreivalType(): CheckboxGroup {
    return this.getInput("retreivalType") as CheckboxGroup;
  }
  set retreivalType(value: CheckboxGroup) {
    this.setInput("retreivalType", value);
  }
  get chaseId(): Textbox {
    return this.getInput("chaseId") as Textbox;
  }
  get chaseStatus(): CheckboxGroup {
    return this.getInput("chaseStatus") as CheckboxGroup;
  }
  set chaseStatus(value: CheckboxGroup) {
    this.setInput("chaseStatus", value);
  }
  get sampleComplianceCode(): CheckboxGroup {
    return this.getInput("sampleComplianceCode") as CheckboxGroup;
  }
  set sampleComplianceCode(value: CheckboxGroup) {
    this.setInput("sampleComplianceCode", value);
  }
  get projectName(): CheckboxGroup {
    return this.getInput("projects") as CheckboxGroup;
  }
  set projectName(value: CheckboxGroup) {
    this.setInput("projects", value);
  }
  get measureCode(): CheckboxGroup {
    return this.getInput("measureCode") as CheckboxGroup;
  }
  set measureCode(value: CheckboxGroup) {
    this.setInput("measureCode", value);
  }
  get chaseSourceAliasId(): Textbox {
    return this.getInput("chaseSourceAliasId") as Textbox;
  }

  getInput<T extends DynamicInput>(key: string): T {
    if (this.serverRequest == null) {
      return null;
    }
    return this.serverRequest.getInput<T>(key);
  }
  setInput<T extends DynamicInput>(key: string, value: T): void {
    if (this.serverRequest == null) {
      return null;
    }
    this.serverRequest.setInput<T>(key, value);
  }

  constructor(@Inject(BASE_API_URL) private readonly baseApiUrl: string,
              private approvalCenterService: ApprovalCenterService,
              private messagingService: MessagingService,
              private changeDetector: ChangeDetectorRef,
              private router: Router,
              private formService: FormService,
              private retrievalPageService: RetrievalPageService,
              private clinicalPageService: ClinicalPageService,
              private readonly chartService: ChartService,
              private chaseQryService: ChaseQueryService,
              private readonly automapper: AutomapperService,
              private retrievalDocumentService: RetrievalDocumentServiceService,
              private readonly tagService: TagService
  ) { }

  ngOnInit() {
    this.createGrids();
    this.setUp();
    this.getstats();
    this.getProjects();
    this.getComplianceCodes();
    this.getStatuses();
    this.getRetreivalTypes();
  }

  createGrids(): void {
    this.serverGridConfiguration.columns = [
      new GridColumnDefinition({ field: "documentQueueId", template: this.docIdColumn, header: "DOCUMENT ID" }),
      new GridColumnDefinition({ field: "retreivalType", header: "RETRIEVAL METHOD" }),
      new GridColumnDefinition({ field: "chaseId", header: "ORIGINAL CHASE ID", template: this.chaseIdColumn }),
      new GridColumnDefinition({ field: "chaseSourceAliasId", header: "CHASE KEY" }),
      new GridColumnDefinition({ field: "measureCode", header: "MEASURE" }),
      new GridColumnDefinition({ field: "chaseStatus", header: "ORIGINAL CHASE STATUS" }),
      new GridColumnDefinition({ field: "sampleComplianceCode", header: "SAMPLE COMPLIANCE" }),
      new GridColumnDefinition({ field: "projectName", header: "PROJECT" }),
      new GridColumnDefinition({ field: "numberOfPages", header: "NEW PAGES" }),
      new GridColumnDefinition({ field: "createDate", header: "UPLOAD DATE", pipeName: GridPipeName.Date, format: "MM-dd-yyyy" }),
    ];

    this.serverGridConfiguration.pageSize = 10;
    this.serverGridConfiguration.selectionMode = "multiple";
    this.serverGridConfiguration.showViews = true;
    this.serverGridConfiguration.stateName = "approval_center_documents";

    this.serverRequest = new GridRequest({
      url: `${this.baseApiUrl}document/approval/search`,
      filters: [
        new GridFilter({
          input: new Textbox(),
          key: "masterDocumentSourceID",
          name: "AID",
        }),
        new GridFilter({
          input: new Textbox(),
          key: "documentQueueId",
          name: "Document ID",
        }),
        new GridFilter({
          input: new CheckboxGroup(),
          key: "retreivalType",
          name: "RETRIEVAL METHOD",
        }),
        new GridFilter({
          input: new Textbox(),
          key: "chaseId",
          name: "ORIGINAL CHASE ID",
        }),
        new GridFilter({
          input: new CheckboxGroup(),
          key: "chaseStatus",
          name: "ORIGINAL CHASE STATUS",
        }),
        new GridFilter({
          input: new CheckboxGroup(),
          key: "sampleComplianceCode",
          name: "SAMPLE COMPLIANCE",
        }),
        new GridFilter({
          input: new CheckboxGroup(),
          key: "projects",
          name: "Projects",
        }),
        new GridFilter({
          input: new CheckboxGroup(),
          key: "measureCode",
          name: "Measures",
        }),
        new GridFilter({
          input: new Textbox(),
          key: "chaseSourceAliasId",
          name: "CHASE KEY",
        }),
      ],
      sortDirection: -1,
      sortField: "documentApprovalId",
    });
    this.actions = this.getActions();
    this.refreshGrid.emit();
  }

  // VIEW/FORM METHODS
  private getActions(): BulkAction[] {
    const bulkActions = [
      new BulkAction({
        name: "View Document",
        action: this.showDocumentModal.bind(this),
        showBulkAction: false,
      }),
      new BulkAction({
        name: "Approve",
        action: (items: any[]) => this.confirmPursuitCreationOnApproval(items),
        showBulkAction: true,
      }),
      new BulkAction({
        name: "Deny",
        action: (items: any[]) => this.denyClick(items),
        showBulkAction: true,
      }),
    ];
    return bulkActions;
  }

  setUp() {

    this.headerTitle = "APPROVE DOCUMENTS";
    this.selectedStat = "Waiting for Approval";

    this.uploadMethods = [
      {
        label: "By Chase", command: () => {
          this.router.navigate(["/project/approvalcenter/upload/chase/byid"]);
        },
      },
      {
        label: "Bulk Upload", command: () => {
          this.router.navigate(["/project/approvalcenter/upload/chase/bulk"]);
        },

      }];
  }

  trackByIndex(index, item) {
    return index;
  }

  // END METHODS

  // VIEW EVENTS

  getStatisticsFilter(filterName: string): void {
    this.statisticsFilter = filterName;

    this.selectedStat = filterName;

  }

  viewDetailsClick(rowData: DocumentApprovalSearchResponse) {
    DocumentPageViewerComponent.documentId.next(rowData.workingDocumentId);
    this.isViewDetailVisible = true;

  }

  approveMultipleDocumentAndCreatePursuit(): void {
    this.showConfirmationForPursuitCreationOnApprove = false;
    this.changeDetector.markForCheck();
    this.setApprovalStateMultipleDocument(this.approvalRowData, DocumentApprovalState.ApprovedForUploaded);
  }


  denyClick(rowData: any) {
    if ((this.documentApprovalGridSelection?.length ?? 0) > 100) {
      this.messagingService.showToast("You can select a maximum of 100 request. Please reduce your selection to proceed.", SeverityType.WARN);
    } else {
      const rows = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];
      this.approvalRowData = rows;
      this.setApprovalStateMultipleDocument(this.approvalRowData, DocumentApprovalState.Denied);
    }
  }

  disableAction(action, rowData: DocumentApprovalSearchResponse): boolean {
    let isDisbaled = true;
    switch (action) {
      case "View Document":
        isDisbaled = false;
        break;
      case "Approve":
        if (rowData.documentApprovalStateId === DocumentApprovalState.WaitingForApproval) {
          isDisbaled = false;
        }
        break;
      case "Deny":
        if (rowData.documentApprovalStateId === DocumentApprovalState.WaitingForApproval || rowData.documentApprovalStateId === DocumentApprovalState.ApprovedForUploaded) {
          isDisbaled = false;
        }
        break;
      default:
        break;
    }
    return isDisbaled;
  }

  // END VIEW EVENTS

  // SERVICE CALLS

  getstats(): void {
    this.approvalCenterService
      .getDocumentApprovalStats()
      .subscribe(items => {
        this.headerStatistics = List(items);
        this.changeDetector.markForCheck();
      });
  }

  setApprovalStateMultipleDocument(documentApprovalRowData: DocumentApprovalSearchResponse[], documentApprovalState: DocumentApprovalState) {
    this.isChaseActivated = false;

    const approvalItems: DocumentApprovalItem[] = documentApprovalRowData.map(
      item => new DocumentApprovalItem({
        documentApprovalId: item.documentApprovalId,
        documentQueueId: item.documentQueueId,
        chaseId: item.chaseId,
        currentDocumentApprovalStateID: item.documentApprovalStateId,
        approvalStatusDirectoryUserID: item.approvalStatusDirectoryUserID,
      })
    );

    const bulkDocumentApprovalRequestModel = new BulkDocumentApprovalRequest({
      documentApprovalStateId: documentApprovalState,
      documentTypeId: DocumentTypeName.DocTypeMedicalRecord,
      documentApprovalItems: approvalItems,
    });

    this.approvalCenterService.setMultipleDocumentApprovalUpdateState(bulkDocumentApprovalRequestModel)
        .subscribe(response => {
          let bulkApprovalMessage = "";
          if (response) {
            const validDocs = response.filter(doc => doc.validationStatus === true);
            const invalidDocs = response.filter(doc => doc.validationStatus === false);
            const approvalStateId = bulkDocumentApprovalRequestModel.documentApprovalStateId;

            if (approvalStateId === DocumentApprovalState.ApprovedForUploaded) {
              const chaseActivatedCount = validDocs.filter(doc => doc.isChaseActivated).length;
              const createdChildChaseCount = new Set(validDocs
                                                      .filter(doc => doc.createdPursuitChaseId > 0)
                                                      .map(doc => doc.createdPursuitChaseId)).size;

              if (chaseActivatedCount > 0 && createdChildChaseCount > 0) {
                bulkApprovalMessage = `${chaseActivatedCount} chase(s) successfully activated and Pages successfully added ${createdChildChaseCount} child chase(s).`;
              } else if (createdChildChaseCount > 0) {
                bulkApprovalMessage = `Pages successfully added ${createdChildChaseCount} child chase(s).`;
              } else if (chaseActivatedCount > 0) {
                bulkApprovalMessage = `${chaseActivatedCount} chase(s) successfully activated.`;
              }

              if (bulkApprovalMessage) {
                this.messagingService.showToast(bulkApprovalMessage, SeverityType.SUCCESS);
              }

              if (invalidDocs.length > 0) {
                this.messagingService.showToast(`Failed to approve ${invalidDocs.length} document(s) request.`, SeverityType.ERROR);
              }

            } else if (approvalStateId === DocumentApprovalState.Denied) {
              if (validDocs.length > 0) {
                this.messagingService.showToast(`${validDocs.length} Approval(s) requests denied.`, SeverityType.SUCCESS);
              }

              if (invalidDocs.length > 0) {
                this.messagingService.showToast(`Failed to deny ${invalidDocs.length} document(s) request.`, SeverityType.ERROR);
              }
            }

          } else {
            const approvalState = bulkDocumentApprovalRequestModel.documentApprovalStateId === DocumentApprovalState.ApprovedForUploaded
              ? "Approve"
              : bulkDocumentApprovalRequestModel.documentApprovalStateId === DocumentApprovalState.Denied
                ? "Denied"
                : "Process";

            this.messagingService.showToast(`Failed to ${approvalState} document.`, SeverityType.ERROR);
          }

          this.getstats();
          this.documentApprovalGridSelection = [];
          this.refreshGrid.emit();
          this.changeDetector.markForCheck();
        },
                   err => {
          this.messagingService.showToast("Failed to Approve/Deny document.", SeverityType.ERROR);
          this.documentApprovalGridSelection = [];
        });
  }

  setApprovalState(documentApprovalId: number, documentApprovalState: DocumentApprovalState) {
    this.isChaseActivated = false;
    this.approvalCenterService.setDocumentApprovalState(documentApprovalId, documentApprovalState, DocumentTypeName.DocTypeMedicalRecord)
      .subscribe(data => {
        const resposne = Object.values(data);
        if (documentApprovalState === DocumentApprovalState.ApprovedForUploaded) {
          this.approvalPursuitChaseId = resposne[0];
          this.isChaseActivated = resposne[1];
          this.showPostApprovalMessage = true;
          this.changeDetector.markForCheck();
        } else if (documentApprovalState === DocumentApprovalState.Denied) {
          this.messagingService.showToast("Approval Denied", SeverityType.SUCCESS);
        }
        this.getstats();
        this.refreshGrid.emit();
      },
                 err => {
          this.messagingService.showToast("Failed to Approve/Deny document.", SeverityType.ERROR);
        }
      );
  }

  confirmPursuitCreationOnApproval(rowData: any): void {
    if ((this.documentApprovalGridSelection?.length ?? 0) > 100) {
      this.messagingService.showToast("You can select a maximum of 100 request. Please reduce your selection to proceed.", SeverityType.WARN);
    } else {
      this.showConfirmationForPursuitCreationOnApprove = true;
      const rows = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];

      this.approvalRowData = rows;

      // Step 1: Convert all chaseStatus values to lowercase for consistent comparison
      const statuses = rows.map(row => row.chaseStatus.toLowerCase());

      // Step 2: Check if 'dataload' is present in the statuses
      const hasDataload = statuses.includes("dataload");

      // Step 3: Check if there are any statuses other than 'dataload'
      const hasOther = statuses.some(status => status !== "dataload");

      // Step 4: Determine the combination of statuses and log the result
      if (hasDataload && hasOther) {
        this.approvePopUpConfirmationText = "By approving these document(s) you will be activating chase(s) or adding documentation to child chase(s). Do you want to continue?";
      } else if (hasDataload) {
        this.approvePopUpConfirmationText = "By approving document(s), you will activate the chase(s). Do you want to continue?";
      } else {
        this.approvePopUpConfirmationText = "By approving document(s), these pages will be added to child chase(s). Do you want to continue?";
      }

      this.changeDetector.markForCheck();
    }
  }

  showDocumentModal(rowData: any): void {
    const rows = ArrayHelper.isAvailable(rowData) ? rowData : [rowData];

    this.modalApprovalRowData = rows;
    this.selectedDocId = rows[0].workingDocumentId;
    this.isDocumentModalVisble = true;
  }

  visibleChange(event: boolean) {
    if (!event) {
      this.selectedDocId = null;
    }
  }

  closeModalAndApproveOrDeny(approveOrDeny: string) {
    this.isDocumentModalVisble = false;
    this.selectedDocId = null;

    if (approveOrDeny === "approve") {
      this.confirmPursuitCreationOnApproval(this.modalApprovalRowData);
    } else if (approveOrDeny === "deny") {
      this.denyClick(this.modalApprovalRowData);
    }
  }

  get url(): string {
    return `/members/chase/${this.approvalPursuitChaseId}`;
  }

  onRemoveFilter(event: IFilter): void {
    if (event.reset) {
      this.form.reset();
    } else if (ArrayHelper.isAvailable(this.form.get(event.text).value)) {
      const filteredValue = this.form.get(event.text).value.filter(item => item.value !== event.value);
      this.form.get(event.text).setValue(filteredValue);
    } else {
      this.form.get(event.text).reset();
    }
  }
  // END SERVICE CALLS


  private getProjects(): void {
    this.retrievalPageService
      .getProjectList()
      .pipe(map((result: any) => {
        return result.map(item => new SelectableInput({
          text: item.description,
          value: item.id,
        }));
      }))
      .subscribe(options => {
        this.projectName = new CheckboxGroup({ ...this.projectName, options } as any);
        this.formService.updateDom.next();
      });
  }

  private getMeasures(): void {
    this.clinicalPageService
      .getMeasuresList()
      .pipe(map((result: any) => {
        return result.map(item => new SelectableInput({
          text: item.measureCode,
          value: item.measureId,
        }));
      }))
      .subscribe(options => {
        this.measureCode = new CheckboxGroup({ ...this.measureCode, options } as any);
        this.formService.updateDom.next();
      });
  }

  private getComplianceCodes(): void {
    this.chartService.getComplianceCodes()
      .subscribe(options => {
        this.sampleComplianceCode = new CheckboxGroup({ ...this.sampleComplianceCode, options } as any);
        this.formService.updateDom.next();
      });
  }

  onApprovalGridDataLoaded(rows: any[]): void {
    rows.forEach(row => {
      if (row.chaseStatus) {
        row.chaseStatus = row.chaseStatus.replace("Client Overread", "Overread II");
      }
    });
  }

  private getStatuses(): void {
    this.chaseQryService
      .getReportingStatusesList()
      .pipe(map(this.automapper.curryMany("ReportingStatusModel", "SelectableInput")))
      .subscribe(options => {
        const mappedOptions = options.map(o => ({ ...o, text: o.text?.replace("Client Overread", "Overread II") }));
        this.chaseStatus = new CheckboxGroup({ ...this.chaseStatus, options: mappedOptions } as any);
        this.formService.updateDom.next();
      });
  }

  private getRetreivalTypes(): void {
    this.retrievalDocumentService
      .getRetrievalTypes()
      .pipe(map((result: any) => {
        return result.map(item => new SelectableInput({
          text: item.text,
          value: item.value,
        }));
      }))
      .subscribe(result => {
        this.retreivalType = new CheckboxGroup({ ...this.retreivalType, options: result } as any);
        this.changeDetector.markForCheck();
      });
  }

  openManageTagModal(rowData?: any): void {
    this.selectedDocuments = [];
    if (rowData != null) {
      this.selectedDocuments.push(rowData);
    } else if (ArrayHelper.isAvailable(this.gridSelection)) {
      this.gridSelection.forEach(item => this.selectedDocuments.push(item));
    }

    if (ArrayHelper.isAvailable(this.selectedDocuments)) {
      this.totalEntityCount = this.selectedDocuments.length;
      this.entityName = "DOCUMENTS";
      this.isManageTagModalVisible = true;
    } else {
      this.messagingService.showToast("Document(s) not in right status to be actioned upon.", SeverityType.WARN);
    }
  }

  closeModal(): void {
    this.selectedDocuments = [];
    this.gridSelection = [];
    this.isManageTagModalVisible = false;
  }

  getAllTagsList(): void {
    this.allAvailableTags = [];
    this.tagService.getAllTagsList(this.tagType, null)
      .subscribe(data => {
        this.allAvailableTags = data
          .filter(e => e.text === "Suggestions")
          .concat(
            data.filter(e => e.text !== "Suggestions")
              .sort((a, b) => a.text.localeCompare(b.text))
          );
      });
  }

  onChangeObject(event): void {
    this.selectedObjectId = event;
  }

  onActiveIndexChange(event): void {
    if (event.header === "Measures") {
      this.getMeasures();
    }
  }
}
